import React, { useState, useEffect, useCallback } from 'react';
import { useOrg } from '../context/OrgContext';
import { usePermissions } from '../components/PermissionGuard';
import { PageHeader } from '../components/PageHeader';
import * as reportProfilesApi from '../api/reportProfiles';

export function ReportProfiles() {
  const { selectedOrg } = useOrg();
  const { isAdmin, canReadAssets } = usePermissions();
  const [profiles, setProfiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [showVersionsModal, setShowVersionsModal] = useState(false);
  const [selectedProfile, setSelectedProfile] = useState(null);
  const [versions, setVersions] = useState([]);
  const [saving, setSaving] = useState(false);
  const [filterStatus, setFilterStatus] = useState('');
  const [form, setForm] = useState({
    name: '',
    description: '',
    programId: 'program-nevi',
    reportTypeId: 'report-type-utility-usage',
    templateId: '',
    mappingConfig: {}
  });
  const [newMapping, setNewMapping] = useState({ key: '', value: '' });

  const loadProfiles = useCallback(async () => {
    if (!selectedOrg || !canReadAssets) {
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);
    try {
      const filters = {};
      if (filterStatus) filters.status = filterStatus;
      const data = await reportProfilesApi.getReportProfiles(selectedOrg.id, filters);
      setProfiles(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [selectedOrg, canReadAssets, filterStatus]);

  useEffect(() => {
    loadProfiles();
  }, [loadProfiles]);

  const handleOpenCreate = () => {
    setSelectedProfile(null);
    setForm({
      name: '',
      description: '',
      programId: 'program-nevi',
      reportTypeId: 'report-type-utility-usage',
      templateId: 'template-1',
      mappingConfig: {}
    });
    setShowModal(true);
  };

  const handleOpenEdit = (profile) => {
    setSelectedProfile(profile);
    setForm({
      name: profile.name,
      description: profile.description || '',
      programId: profile.programId,
      reportTypeId: profile.reportTypeId,
      templateId: profile.templateId || '',
      mappingConfig: { ...profile.mappingConfig }
    });
    setShowModal(true);
  };

  const handleViewVersions = async (profile) => {
    if (!selectedOrg) return;
    try {
      const vers = await reportProfilesApi.getReportProfileVersions(selectedOrg.id, profile.id);
      setVersions(vers);
      setSelectedProfile(profile);
      setShowVersionsModal(true);
    } catch (err) {
      setError(err.message);
    }
  };

  const handleSave = async () => {
    if (!selectedOrg || saving) return;
    setSaving(true);
    try {
      if (selectedProfile) {
        await reportProfilesApi.updateReportProfile(selectedOrg.id, selectedProfile.id, {
          name: form.name,
          description: form.description,
          templateId: form.templateId,
          mappingConfig: form.mappingConfig
        });
      } else {
        await reportProfilesApi.createReportProfile(selectedOrg.id, form);
      }
      setShowModal(false);
      loadProfiles();
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const handlePublish = async (profile) => {
    if (!selectedOrg) return;
    try {
      await reportProfilesApi.publishReportProfile(selectedOrg.id, profile.id);
      loadProfiles();
    } catch (err) {
      setError(err.message);
    }
  };

  const handleAddMapping = () => {
    if (!newMapping.key || !newMapping.value) return;
    setForm(prev => ({
      ...prev,
      mappingConfig: {
        ...prev.mappingConfig,
        [newMapping.key]: newMapping.value
      }
    }));
    setNewMapping({ key: '', value: '' });
  };

  const handleRemoveMapping = (key) => {
    setForm(prev => {
      const newConfig = { ...prev.mappingConfig };
      delete newConfig[key];
      return { ...prev, mappingConfig: newConfig };
    });
  };

  if (!canReadAssets) {
    return (
      <div style={styles.container}>
        <div style={styles.accessDenied}>
          <h2>Access Denied</h2>
          <p>You do not have permission to view report profiles.</p>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      <PageHeader
        title="Report Profiles"
        subtitle="Configure and publish report profiles"
      />

      <div style={styles.toolbar}>
        <select
          value={filterStatus}
          onChange={(e) => setFilterStatus(e.target.value)}
          style={styles.select}
          data-testid="status-filter"
        >
          <option value="">All Status</option>
          <option value="draft">Draft</option>
          <option value="published">Published</option>
        </select>
        {isAdmin && (
          <button
            onClick={handleOpenCreate}
            style={styles.createButton}
            data-testid="create-profile-button"
          >
            + New Profile
          </button>
        )}
      </div>

      {error && (
        <div style={styles.error}>{error}</div>
      )}

      {loading ? (
        <div style={styles.loading}>Loading profiles...</div>
      ) : (
        <div style={styles.tableContainer}>
          <table style={styles.table} data-testid="profiles-table">
            <thead>
              <tr>
                <th style={styles.th}>Name</th>
                <th style={styles.th}>Program</th>
                <th style={styles.th}>Report Type</th>
                <th style={styles.th}>Status</th>
                <th style={styles.th}>Version</th>
                <th style={styles.th}>Updated</th>
                <th style={styles.th}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {profiles.map(profile => (
                <tr key={profile.id} style={styles.tr} data-testid={`profile-${profile.id}`}>
                  <td style={styles.td}>
                    <div style={styles.profileName}>{profile.name}</div>
                    <div style={styles.profileDesc}>{profile.description}</div>
                  </td>
                  <td style={styles.td}>{profile.programId}</td>
                  <td style={styles.td}>{profile.reportTypeId}</td>
                  <td style={styles.td}>
                    <span style={{
                      ...styles.statusBadge,
                      background: profile.status === 'published' ? '#c6f6d5' : '#fefcbf',
                      color: profile.status === 'published' ? '#276749' : '#975a16'
                    }}>
                      {profile.status}
                    </span>
                  </td>
                  <td style={styles.td}>v{profile.currentVersion}</td>
                  <td style={styles.td}>{new Date(profile.updatedAt).toLocaleDateString()}</td>
                  <td style={styles.td}>
                    <button
                      onClick={() => handleViewVersions(profile)}
                      style={styles.actionButton}
                      data-testid={`versions-${profile.id}`}
                    >
                      Versions
                    </button>
                    {isAdmin && profile.status === 'draft' && (
                      <>
                        <button
                          onClick={() => handleOpenEdit(profile)}
                          style={styles.editButton}
                          data-testid={`edit-${profile.id}`}
                        >
                          Edit
                        </button>
                        <button
                          onClick={() => handlePublish(profile)}
                          style={styles.publishButton}
                          data-testid={`publish-${profile.id}`}
                        >
                          Publish
                        </button>
                      </>
                    )}
                  </td>
                </tr>
              ))}
              {profiles.length === 0 && (
                <tr>
                  <td colSpan="7" style={styles.emptyState}>No profiles found</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {showModal && (
        <div style={styles.modalOverlay}>
          <div style={styles.modal} data-testid="profile-modal">
            <h3 style={styles.modalTitle}>
              {selectedProfile ? 'Edit Profile' : 'New Report Profile'}
            </h3>
            <div style={styles.formGroup}>
              <label style={styles.label}>Name</label>
              <input
                type="text"
                value={form.name}
                onChange={(e) => setForm(prev => ({ ...prev, name: e.target.value }))}
                style={styles.input}
                data-testid="profile-name-input"
              />
            </div>
            <div style={styles.formGroup}>
              <label style={styles.label}>Description</label>
              <input
                type="text"
                value={form.description}
                onChange={(e) => setForm(prev => ({ ...prev, description: e.target.value }))}
                style={styles.input}
                data-testid="profile-description-input"
              />
            </div>
            {!selectedProfile && (
              <div style={styles.formRow}>
                <div style={styles.formGroup}>
                  <label style={styles.label}>Program</label>
                  <select
                    value={form.programId}
                    onChange={(e) => setForm(prev => ({ ...prev, programId: e.target.value }))}
                    style={styles.select}
                    data-testid="profile-program-input"
                  >
                    <option value="program-nevi">NEVI</option>
                    <option value="program-fleet">Fleet</option>
                  </select>
                </div>
                <div style={styles.formGroup}>
                  <label style={styles.label}>Report Type</label>
                  <select
                    value={form.reportTypeId}
                    onChange={(e) => setForm(prev => ({ ...prev, reportTypeId: e.target.value }))}
                    style={styles.select}
                    data-testid="profile-report-type-input"
                  >
                    <option value="report-type-utility-usage">Utility Usage</option>
                    <option value="report-type-usage-summary">Usage Summary</option>
                  </select>
                </div>
              </div>
            )}
            <div style={styles.formGroup}>
              <label style={styles.label}>Template ID</label>
              <input
                type="text"
                value={form.templateId}
                onChange={(e) => setForm(prev => ({ ...prev, templateId: e.target.value }))}
                style={styles.input}
                placeholder="e.g., template-1"
                data-testid="profile-template-input"
              />
            </div>
            <h4 style={styles.sectionTitle}>Mapping Configuration</h4>
            <div style={styles.mappingList}>
              {Object.entries(form.mappingConfig).map(([key, value]) => (
                <div key={key} style={styles.mappingItem}>
                  <span style={styles.mappingKey}>{key}</span>
                  <span style={styles.mappingArrow}>→</span>
                  <span style={styles.mappingValue}>{value}</span>
                  <button
                    onClick={() => handleRemoveMapping(key)}
                    style={styles.removeButton}
                  >
                    ×
                  </button>
                </div>
              ))}
            </div>
            <div style={styles.addMappingRow}>
              <input
                type="text"
                placeholder="Output field"
                value={newMapping.key}
                onChange={(e) => setNewMapping(prev => ({ ...prev, key: e.target.value }))}
                style={styles.smallInput}
                data-testid="mapping-key-input"
              />
              <input
                type="text"
                placeholder="Source path"
                value={newMapping.value}
                onChange={(e) => setNewMapping(prev => ({ ...prev, value: e.target.value }))}
                style={styles.smallInput}
                data-testid="mapping-value-input"
              />
              <button
                onClick={handleAddMapping}
                style={styles.addButton}
                data-testid="add-mapping-button"
              >
                Add
              </button>
            </div>
            <div style={styles.modalActions}>
              <button onClick={() => setShowModal(false)} style={styles.cancelButton}>
                Cancel
              </button>
              <button
                onClick={handleSave}
                disabled={saving || !form.name}
                style={styles.submitButton}
                data-testid="save-profile-button"
              >
                {saving ? 'Saving...' : 'Save'}
              </button>
            </div>
          </div>
        </div>
      )}

      {showVersionsModal && selectedProfile && (
        <div style={styles.modalOverlay}>
          <div style={styles.modal} data-testid="versions-modal">
            <h3 style={styles.modalTitle}>Version History: {selectedProfile.name}</h3>
            <table style={styles.versionsTable}>
              <thead>
                <tr>
                  <th style={styles.th}>Version</th>
                  <th style={styles.th}>Template</th>
                  <th style={styles.th}>Published</th>
                </tr>
              </thead>
              <tbody>
                {versions.map(v => (
                  <tr key={v.id} style={styles.tr}>
                    <td style={styles.td}>v{v.version}</td>
                    <td style={styles.td}>{v.templateId}</td>
                    <td style={styles.td}>{new Date(v.publishedAt).toLocaleString()}</td>
                  </tr>
                ))}
                {versions.length === 0 && (
                  <tr>
                    <td colSpan="3" style={styles.emptyState}>No versions published yet</td>
                  </tr>
                )}
              </tbody>
            </table>
            <div style={styles.modalActions}>
              <button onClick={() => setShowVersionsModal(false)} style={styles.cancelButton}>
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const styles = {
  container: { padding: '2rem', maxWidth: '1400px', margin: '0 auto' },
  accessDenied: { padding: '3rem', textAlign: 'center', background: '#fff', borderRadius: '8px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' },
  toolbar: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' },
  select: { padding: '0.5rem 1rem', borderRadius: '6px', border: '1px solid #e2e8f0', fontSize: '0.875rem', background: 'white' },
  createButton: { padding: '0.5rem 1rem', borderRadius: '6px', border: '1px solid #3182ce', background: '#3182ce', color: 'white', fontSize: '0.875rem', cursor: 'pointer' },
  error: { padding: '1rem', background: '#fed7d7', color: '#c53030', borderRadius: '8px', marginBottom: '1rem' },
  loading: { padding: '2rem', textAlign: 'center', color: '#718096' },
  tableContainer: { background: 'white', borderRadius: '8px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)', overflow: 'auto' },
  table: { width: '100%', borderCollapse: 'collapse' },
  th: { textAlign: 'left', padding: '1rem', background: '#f7fafc', borderBottom: '1px solid #e2e8f0', fontWeight: '600', fontSize: '0.875rem', color: '#4a5568' },
  tr: { borderBottom: '1px solid #e2e8f0' },
  td: { padding: '1rem', fontSize: '0.875rem' },
  profileName: { fontWeight: '500' },
  profileDesc: { fontSize: '0.75rem', color: '#718096' },
  statusBadge: { padding: '0.25rem 0.5rem', borderRadius: '4px', fontSize: '0.75rem', textTransform: 'capitalize' },
  actionButton: { padding: '0.25rem 0.5rem', borderRadius: '4px', border: '1px solid #718096', background: 'white', color: '#718096', fontSize: '0.75rem', cursor: 'pointer', marginRight: '0.5rem' },
  editButton: { padding: '0.25rem 0.5rem', borderRadius: '4px', border: '1px solid #3182ce', background: 'white', color: '#3182ce', fontSize: '0.75rem', cursor: 'pointer', marginRight: '0.5rem' },
  publishButton: { padding: '0.25rem 0.5rem', borderRadius: '4px', border: '1px solid #48bb78', background: '#48bb78', color: 'white', fontSize: '0.75rem', cursor: 'pointer' },
  emptyState: { padding: '2rem', textAlign: 'center', color: '#718096' },
  modalOverlay: { position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 },
  modal: { background: 'white', borderRadius: '8px', padding: '1.5rem', minWidth: '600px', maxWidth: '700px', maxHeight: '80vh', overflow: 'auto', boxShadow: '0 4px 6px rgba(0,0,0,0.1)' },
  modalTitle: { margin: '0 0 1.5rem 0', fontSize: '1.25rem', fontWeight: '600' },
  sectionTitle: { margin: '1.5rem 0 0.75rem 0', fontSize: '0.875rem', fontWeight: '600', color: '#4a5568' },
  formGroup: { marginBottom: '1rem', flex: 1 },
  formRow: { display: 'flex', gap: '1rem' },
  label: { display: 'block', marginBottom: '0.25rem', fontSize: '0.875rem', fontWeight: '500', color: '#4a5568' },
  input: { width: '100%', padding: '0.5rem', borderRadius: '6px', border: '1px solid #e2e8f0', fontSize: '0.875rem' },
  mappingList: { marginBottom: '1rem' },
  mappingItem: { display: 'flex', alignItems: 'center', gap: '0.5rem', padding: '0.5rem', background: '#f7fafc', borderRadius: '4px', marginBottom: '0.25rem' },
  mappingKey: { fontWeight: '500', minWidth: '120px' },
  mappingArrow: { color: '#718096' },
  mappingValue: { flex: 1, color: '#4a5568', fontFamily: 'monospace', fontSize: '0.75rem' },
  removeButton: { padding: '0.125rem 0.375rem', border: 'none', background: '#fed7d7', color: '#c53030', borderRadius: '4px', cursor: 'pointer' },
  addMappingRow: { display: 'flex', gap: '0.5rem', alignItems: 'center' },
  smallInput: { flex: 1, padding: '0.375rem', borderRadius: '4px', border: '1px solid #e2e8f0', fontSize: '0.75rem' },
  addButton: { padding: '0.375rem 0.75rem', borderRadius: '4px', border: '1px solid #48bb78', background: '#48bb78', color: 'white', fontSize: '0.75rem', cursor: 'pointer' },
  versionsTable: { width: '100%', borderCollapse: 'collapse', marginBottom: '1rem' },
  modalActions: { display: 'flex', gap: '0.5rem', justifyContent: 'flex-end', marginTop: '1.5rem' },
  cancelButton: { padding: '0.5rem 1rem', borderRadius: '6px', border: '1px solid #e2e8f0', background: 'white', fontSize: '0.875rem', cursor: 'pointer' },
  submitButton: { padding: '0.5rem 1rem', borderRadius: '6px', border: '1px solid #3182ce', background: '#3182ce', color: 'white', fontSize: '0.875rem', cursor: 'pointer' }
};
